import React from 'react';
import { useIntl } from 'react-intl';
import { MdPublish } from 'react-icons/md';
import { Button, IconButton } from '@aibuild/ui';
import type { UserAppDraft } from '@api/appBuilder';

interface PublishButtonProps {
  readonly draft: UserAppDraft;
  readonly isPublishing: boolean;
  readonly isRestoring: boolean;
  readonly onPublish: () => void;
  readonly iconOnly?: boolean;
}

const PublishButton: React.FC<PublishButtonProps> = ({ draft, isPublishing, isRestoring, onPublish, iconOnly = false }) => {
  const intl = useIntl();
  const nothingNew = draft.publishedVersion != null && !draft.hasUnpublishedChanges;
  const disabled = draft.status === 'DISABLED' || nothingNew || isRestoring;
  const label = intl.formatMessage({ id: 'apps.builder.publish', defaultMessage: 'Publish' });
  const tooltip =
    nothingNew &&
    intl.formatMessage(
      { id: 'apps.builder.publish.nothingNew', defaultMessage: 'No changes since v{version}' },
      { version: draft.publishedVersion },
    );

  if (iconOnly) {
    return (
      <IconButton
        ariaLabel={label}
        tooltip={tooltip || label}
        icon={<MdPublish size={18} aria-hidden="true" />}
        variant="filled"
        disabled={disabled || isPublishing}
        onClick={onPublish}
        dataTestId="app-builder__publish"
      />
    );
  }
  return (
    <Button
      variant="filled"
      leadingIcon={<MdPublish size={16} aria-hidden="true" />}
      loading={isPublishing}
      disabled={disabled}
      tooltip={tooltip}
      onClick={onPublish}
      dataTestId="app-builder__publish"
    >
      {label}
    </Button>
  );
};

export default PublishButton;
